/**
 * AdminModeBanner — persistent visual cue for elevated session
 * states (#429 Wave 2).
 *
 * Two independent states, each with its own bar. Both can be active
 * simultaneously (rare but possible: a superadmin who has impersonated
 * another user AND opted into superadmin mode in the new session). The
 * bars stack in that case — visual nesting matches semantic nesting.
 *
 *   - Red    — `x-superadmin-mode: on` is being sent on requests.
 *              Driven by the in-tab `superadminMode` toggle owned by
 *              `auth/superadminMode.ts`. Indicates platform-wide
 *              escalation that bypasses normal matrix checks on
 *              admin-tier routes.
 *   - Amber  — the active session was created via Better Auth's
 *              admin impersonation primitive. The frontend sees this
 *              as `identity.impersonation.actorUserId` on the
 *              `/api/me` payload. Indicates that actions taken in
 *              this tab are attributed to the impersonated user in
 *              normal audit/membership systems.
 *
 * Mounted ONCE inside PageLayout, above the route Outlet, so it
 * appears on every authenticated screen including /admin/*. Not
 * inside Header so route layouts without a Header (none today, but
 * keeps the option open) still surface the warning.
 *
 * Color tokens reuse the existing semantic palette (`destructive`
 * for red, `warning` for amber) — no new design system primitives.
 */
import { useIdentity } from '@/hooks/useIdentity'
import { useSuperadminMode } from '@/hooks/useSuperadminMode'

export function AdminModeBanner() {
  const { state } = useIdentity()
  const superadminMode = useSuperadminMode()

  const impersonation =
    state.status === 'ready' ? state.identity.impersonation ?? null : null

  // Nothing to render in the common case — every authenticated
  // non-escalated user has both branches off and the component
  // collapses to null. Keeps the DOM clean and avoids an empty
  // wrapper that would still consume layout space.
  if (!superadminMode && !impersonation) return null

  return (
    <div className="sticky top-14 z-40">
      {superadminMode && (
        <div
          role="status"
          aria-live="polite"
          className="bg-destructive text-destructive-foreground px-4 py-1.5 text-xs font-medium text-center"
        >
          Global Admin mode is ON — your requests can reach admin-tier
          routes that the normal matrix would block, and each access
          is logged server-side.
        </div>
      )}
      {impersonation && (
        <div
          role="status"
          aria-live="polite"
          className="bg-warning text-warning-foreground px-4 py-1.5 text-xs font-medium text-center"
        >
          Impersonating another user (acting superadmin:{' '}
          <span className="font-mono">{impersonation.actorUserId}</span>) —
          actions are attributed to the impersonated identity.
        </div>
      )}
    </div>
  )
}
